import React, { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import candidateService from '../../../services/candidateApi'; // IMPORTATION DE TON SERVICE PROPRE
import Topbar from '../../Topbar';
import { EditIcon, DbIcon, ApiIcon } from '../theme';
import { useTheme } from '../../ThemeContext';
import useResponsive from '../ResponsiveHook';

// ── Animations ───────────────────
const containerVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.1 } }
};

const itemVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: { y: 0, opacity: 1, transition: { duration: 0.4, ease: "easeOut" } }
};

// ── Internal Components ───────────────────
function Card({ children, style = {} }) {
  const { C } = useTheme();
  return (
    <motion.div 
      variants={itemVariants}
      whileHover={{ y: -4, transition: { duration: 0.2 } }}
      style={{ background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 12, padding: 20, ...style }}
    >
      {children}
    </motion.div>
  );
}

function SectionHeader({ title, sub, action = "View all", onActionClick }) {
  const { C } = useTheme();
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ display: "flex", alignItems: "center", justifySides: "space-between", justifyContent: "space-between", marginBottom: 4 }}>
        <span style={{ fontSize: 14, fontWeight: 700, color: C.textPrimary }}>{title}</span>
        <motion.button 
          whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}
          onClick={onActionClick}
          style={{
            fontSize: 12, color: C.accent, background: C.accentDim,
            border: `1px solid rgba(167,139,250,0.22)`, borderRadius: 6, padding: "4px 10px", cursor: "pointer", fontWeight: 500,
          }}
        >
          {action}
        </motion.button>
      </div>
      <p style={{ fontSize: 11.5, color: C.textSecond }}>{sub}</p>
    </div>
  );
}

function StatusBadge({ status }) {
  const { C } = useTheme();
  const config = {
    accepted: { color: C.green || '#10b981', label: "ACCEPTED", bg: "rgba(16,185,129,0.1)" },
    pending: { color: C.amber || '#f59e0b', label: "PENDING", bg: "rgba(245,158,11,0.1)" },
    rejected: { color: C.red || '#ef4444', label: "REJECTED", bg: "rgba(239,68,68,0.1)" },
  };
  const s = config[status] || config.pending;
  return (
    <span style={{ 
      fontSize: 10, fontWeight: 700, padding: "3px 8px", borderRadius: 4,
      background: s.bg, color: s.color, border: `1px solid ${s.color}`, textTransform: 'uppercase'
    }}>{s.label}</span>
  );
}

// ── Dashboard Page ────────────────────────────────────────
export default function Dashboard() {
  const { C } = useTheme();
  const { isMobile} = useResponsive();
  const navigate = useNavigate();
  const testIcons = [<EditIcon />, <DbIcon />, <ApiIcon />];
  const { onMenuClick } = useOutletContext();
  const [profile, setProfile] = useState(null);
  const [statsData, setStatsData] = useState(null);
  const [savedTests, setSavedTests] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        const [profileData, candidateData, savedTestsData, submissionsData] = await Promise.all([
          candidateService.getProfile(),
          candidateService.getCandidateData(),
          candidateService.getSavedTests(),
          candidateService.getSubmissions()
        ]);

        if (profileData) setProfile(profileData);
        if (savedTestsData) setSavedTests(savedTestsData);

        // Fetch local submissions and calculate merged list first
        const localSubs = JSON.parse(localStorage.getItem('local_submissions') || '[]');
        let mergedSubmissions = submissionsData ? [...submissionsData] : [];
        const uniqueLocalSubs = [];

        localSubs.forEach(ls => {
          const alreadyExists = mergedSubmissions.some(ms => String(ms.id) === String(ls.id) || String(ms.job_name) === String(ls.job_name));
          if (!alreadyExists) {
            mergedSubmissions.unshift(ls);
            uniqueLocalSubs.push(ls);
          }
        });
        setSubmissions(mergedSubmissions);

        // Calculate merged statistics dynamically
        let mergedStats = candidateData ? { ...candidateData } : { submitted_tests: 0, average_score: 0, global_rank: '24', achievements: 0 };
        if (uniqueLocalSubs.length > 0) {
          const currentCount = mergedStats.submitted_tests || 0;
          const currentAvg = mergedStats.average_score || 0;
          const newCount = currentCount + uniqueLocalSubs.length;

          let totalScore = currentCount * currentAvg;
          uniqueLocalSubs.forEach(ls => {
            totalScore += (ls.score || 95);
          });
          const newAvg = Math.round(totalScore / newCount);

          mergedStats.submitted_tests = newCount;
          mergedStats.average_score = newAvg;
          mergedStats.achievements = (mergedStats.achievements || 0) + uniqueLocalSubs.length;

          if (mergedStats.global_rank) {
            let numericRank = parseInt(String(mergedStats.global_rank).replace('#', ''), 10);
            if (!isNaN(numericRank) && numericRank > 1) {
              numericRank = Math.max(1, numericRank - (uniqueLocalSubs.length * 2));
              mergedStats.global_rank = String(numericRank);
            }
          }
        }
        setStatsData(mergedStats);

      } catch (err) {
        console.error("Dashboard data fetch error:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchDashboardData();
  }, []);

  // Action pour retirer un test des favoris (Appel API DELETE)
  const handleUnsave = async (e, id) => {
    e.stopPropagation(); // Évite de déclencher la redirection vers le détail du test
    try {
      await candidateService.deleteSavedTest(id);
      // Supprime visuellement le test de la liste sans recharger la page
      setSavedTests(prev => prev.filter(test => test.id !== id));
    } catch (err) {
      console.error("Error unsaving test:", err);
    }
  };

  if (loading) {
    return (
      <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", background: C.bgDeep, color: C.textSecond }}>
        <motion.div animate={{ rotate: 360 }} transition={{ repeat: Infinity, duration: 1 }}>⏳</motion.div>
        <span style={{ marginLeft: 10 }}>Loading your secure dashboard...</span>
      </div>
    );
  }

  const userData = profile || { first_name: 'Candidate', last_name: '' };
  const stats = statsData || { submitted_tests: 0, average_score: 0, global_rank: '0', achievements: 0 };
  const initials = `${userData.first_name?.[0] || ''}${userData.last_name?.[0] || ''}`.toUpperCase();

  const dynamicStatsArr = [
    { num: stats.submitted_tests, label: "Submitted Tests" },
    { num: `${stats.average_score}%`, label: "Average Score" },
    { num: `#${stats.global_rank}`, label: "Global Rank" },
    { num: stats.achievements, label: "Achievements" },
  ];

  return (
    <div style={{ 
      height: "100vh", 
      flex: 1, 
      display: "flex", 
      flexDirection: "column", 
      background: C.bgDeep, 
      overflowY: "auto" 
    }}>
      <Topbar title="Dashboard" onMenuClick={onMenuClick}/>
      
      <motion.div 
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        style={{ padding: "24px 28px", display: "flex", flexDirection: "column", gap: 20, paddingBottom: 60 }}
      >
        {/* Welcome Header */}
        <motion.div 
          variants={itemVariants}
          style={{
            background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 12, 
            padding: "18px 24px", position: "relative", overflow: "hidden"
          }}
        >
          <motion.div 
            initial={{ x: "-100%" }} animate={{ x: "0%" }} transition={{ duration: 1 }}
            style={{ position: "absolute", top: 0, left: 0, right: 0, height: 2, background: C.gradBar }} 
          />
          <p style={{ fontSize: 20, fontWeight: 700, color: C.textPrimary, marginBottom: 5 }}>
            Welcome back, {userData.first_name}
          </p>
          <div style={{ display: "flex", alignItems: "center", gap: 16, fontSize: 12.5, color: C.textSecond }}>
            <span>🔥 <strong style={{ color: C.textPrimary }}>Active Candidate</strong></span>
            <span style={{ color: C.accent }}>● {submissions.length} assessments completed</span>
          </div>
        </motion.div>

        {/* Profile & Stats Card */}
        <Card>
          <div style={{ display: "flex", alignItems: "flex-start", gap: 18, marginBottom: 16 }}>
            <div style={{
              width: 56, height: 56, borderRadius: 12, background: C.gradMain,
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 20, fontWeight: 700, color: "#fff",
            }} Mention>{initials}</div>

            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 17, fontWeight: 700, color: C.textPrimary, marginBottom: 3 }}>
                {userData.first_name} {userData.last_name}
              </div>
              <div style={{ fontSize: 12.5, color: C.textSecond, marginBottom: 10 }}>
                {userData.bio || "Candidate Profile"}
              </div>
              <div style={{ display: "flex", gap: 7, flexWrap: "wrap" }}>
                {userData.skills?.map(s => (
                  <span key={s.id} style={{
                    padding: "3px 10px", borderRadius: 5, fontSize: 11.5, fontWeight: 500,
                    background: C.accentDim, border: `1px solid rgba(167,139,250,0.28)`, color: C.accentBright,
                  }}>{s.name}</span>
                ))}
              </div>
            </div>

            {!isMobile && (
              <div style={{ textAlign: "right" }}>
                <div style={{ fontSize: 9, textTransform: "uppercase", color: C.textSecond }}>Ranking</div>
                <div style={{ fontSize: 32, fontWeight: 700, color: C.accent }}>{stats.global_rank}</div>
              </div>
            )}
          </div>
          
          <div style={{ 
            display: "grid", 
            gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(4, 1fr)",
            gap: 12, marginTop: 16,
          }}>
            {dynamicStatsArr.map(({ num, label }) => (
              <div key={label} style={{ background: "rgba(124, 58, 237, 0.08)", border: `1px solid ${C.border}`, borderRadius: 10, padding: "14px", textAlign: "center" }}>
                <div style={{ fontSize: 20, fontWeight: 700, color: C.textPrimary }}>{num}</div>
                <div style={{ fontSize: 9, textTransform: "uppercase", color: C.textSecond, marginTop: 3 }}>{label}</div>
              </div>
            ))}
          </div>
        </Card>

        {/* Saved Tests Grid */}
        {savedTests.length > 0 && (
          <Card>
            <SectionHeader title="Saved Tests" sub="Tests you've bookmarked" onActionClick={() => navigate('/dashboard/saved-tests')} />
            <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(auto-fill, minmax(300px, 1fr))", gap: 10 }}>
              {savedTests.slice(0, 3).map((item, i) => (
                <div key={item.id} onClick={() => navigate(`/dashboard/test/${item.id}`)} style={{
                  display: "flex", alignItems: "center", gap: 12, padding: 12, borderRadius: 10,
                  border: `1px solid ${C.border}`, background: C.bgItem, cursor: "pointer",
                  position: "relative"
                }}>
                  <div style={{ width: 32, height: 32, borderRadius: 6, background: C.accentDim, display: "flex", alignItems: "center", justifyContent: "center", color: C.accent }}>
                    {testIcons[i % 3]}
                  </div>
                  <div style={{ flex: 1, minWidth: 0, paddingRight: 30 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: C.textPrimary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.job_name}</div>
                    <div style={{ fontSize: 11, color: C.textSecond }}>{item.company_name}</div>
                  </div>

                  {/* BOUTON DELETE / UNSAVE INJECTÉ ICI */}
                  <motion.button
                    whileHover={{ scale: 1.1, backgroundColor: "rgba(239, 68, 68, 0.15)", color: "#ef4444" }}
                    whileTap={{ scale: 0.9 }}
                    onClick={(e) => handleUnsave(e, item.id)}
                    title="Remove from bookmarks"
                    style={{
                      position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)",
                      background: "rgba(255, 255, 255, 0.02)", border: `1px solid ${C.border}`,
                      borderRadius: 6, width: 26, height: 26, display: "flex", alignItems: "center",
                      justifyContent: "center", color: C.textMuted, cursor: "pointer", transition: "color 0.1s"
                    }}
                  >
                    ✕
                  </motion.button>
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* Recent Results Table */}
        <Card>
          <SectionHeader 
            title="Recent Submissions" 
            sub="Status of your last applications" 
            action="All submissions" 
            onActionClick={() => navigate('/dashboard/results')} 
          />
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ textAlign: "left", borderBottom: `1px solid ${C.border}` }}>
                  <th style={{ padding: "10px 0", fontSize: 11, color: C.textMuted }}>TEST</th>
                  <th style={{ padding: "10px 0", fontSize: 11, color: C.textMuted }}>COMPANY</th>
                  <th style={{ padding: "10px 0", textAlign: "right", fontSize: 11, color: C.textMuted }}>STATUS</th>
                </tr>
              </thead>
              <tbody>
                {submissions.slice(0, 5).map((sub) => (
                  <tr key={sub.id} style={{ borderBottom: `1px solid ${C.border}` }}>
                    <td style={{ padding: "14px 0", fontSize: 13, color: C.textPrimary }}>{sub.job_name}</td>
                    <td style={{ padding: "14px 0", fontSize: 12, color: C.textSecond }}>{sub.company_name}</td>
                    <td style={{ padding: "14px 0", textAlign: "right" }}>
                      <StatusBadge status={sub.status} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      </motion.div>
    </div>
  );
}